import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import Cookies from 'js-cookie';
import { useNotificationsStore } from './notifications';

export interface Organization {
  id: string;
  name: string;
  description?: string | null;
  logo_url?: string | null;
}

export interface OrganizationMembership {
  id: string;
  role: string;
  organization: Organization;
}

export interface OrganizationInvitation {
  id: string;
  organization: Organization;
}

export interface UserPlan {
  id: string;
  name: string;
  description: string;
  cruise_limit: number;
}

/** One optional email notification topic (e.g. cruise_published, contact_message). */
export interface NotificationTopicOption {
  topic: string;
  label: string;
  enabled: boolean;
}

/** Notification preferences (push + email channels). Legacy disabled_notification_topics = disable both. */
export interface NotificationPreferences {
  optional_topics: NotificationTopicOption[];
  /** Legacy: disables both push and email when present. */
  disabled_notification_topics?: string[];
  disabled_push_notification_topics?: string[];
  disabled_email_notification_topics?: string[];
}

export interface AuthUser {
  id: string;
  provider: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  created_at: string;
  is_master_admin: boolean;
  is_active: boolean;
  language: string | null;
  memberships: OrganizationMembership[];
  invitation?: OrganizationInvitation[];
  plan?: UserPlan;
  is_two_factor_enabled?: boolean;
  /** When false, push notifications (FCM) are disabled for this user. Undefined/true = enabled. */
  notifications_enabled?: boolean;
  /** True if the user has at least one FCM token registered. */
  has_fcm_tokens?: boolean;
  /** Email notification topic preferences (optional_topics + disabled_notification_topics). */
  notification_preferences?: NotificationPreferences;
}

interface AuthState {
  user: AuthUser | null;
  isLoggedIn: boolean;
  authLoading: boolean;
  activeOrganizationId: string | null;
  // Actions
  authorizedFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
  fetchUser: (options?: { silent?: boolean; lang?: string }) => Promise<void>;
  patchUser: (userDataToUpdate: Partial<AuthUser> | Record<string, unknown>, options?: { lang?: string }) => Promise<{ success: boolean; message: string }>;
  setActiveOrganization: (orgId: string | null) => void;
      createOrganization: (organizationData: {
        name: string;
        slug?: string;
        description?: string;
        vat_number?: string;
        website?: string;
        logo?: File;
        contact_email?: string;
        physical_address?: string;
      }) => Promise<{ success: boolean; message?: string; data?: unknown; fieldErrors?: Record<string, string[]> }>;
  loadActiveOrganizationFromCookie: () => void;
  clearAuthState: () => void;
  clearAuthCookies: () => void;
  logout: (redirectToLogin?: boolean) => Promise<void>;
  // Getters (computed)
  isAuthenticated: () => boolean;
  memberships: () => OrganizationMembership[];
  getActiveOrganization: () => OrganizationMembership | null;
  isActiveOrganizationAdmin: () => boolean;
}

// Removed direct backend URL usage - now using BFF routes
const PRIVATE_MODE_COOKIE_VALUE = '__private__';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoggedIn: false,
      authLoading: true, // Start as true so pages wait for auth check to complete
      activeOrganizationId: null,

      // BFF Pattern: Authentication is session-based via HttpOnly cookies
      // No tokens are stored in the frontend - backend manages session
      isAuthenticated: () => get().isLoggedIn,
      memberships: () => get().user?.memberships ?? [],
      getActiveOrganization: () => {
        const state = get();
        if (!state.activeOrganizationId || !state.user?.memberships || !state.user.memberships.length) {
          return null;
        }
        return state.user.memberships.find(
          (membership) => membership.organization.id === state.activeOrganizationId,
        ) ?? null;
      },
      isActiveOrganizationAdmin: () => {
        const activeOrg = get().getActiveOrganization();
        return !!activeOrg && activeOrg.role === 'admin';
      },

      authorizedFetch: async (input: RequestInfo, init: RequestInit = {}): Promise<Response> => {
        const headers = new Headers(init.headers || {});
        if (!headers.has('Accept')) {
          headers.set('Accept', 'application/json');
        }
        // Don't set Content-Type for FormData - browser will set it automatically with boundary
        if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
          headers.set('Content-Type', 'application/json');
        }

        const response = await fetch(input, {
          ...init,
          headers,
          credentials: 'include',
        });

        if (response.status === 401) {
          get().clearAuthState();
        }

        return response;
      },

      fetchUser: async (options?: { silent?: boolean; lang?: string }) => {
        const silent = options?.silent === true;
        if (!silent) set({ authLoading: true });

        try {
          // Use BFF route; ?lang= ensures notification preference labels are translated
          const lang = options?.lang && (options.lang === 'en' || options.lang === 'pl') ? options.lang : undefined;
          const apiUrl = lang ? `/api/auth/me?lang=${lang}` : '/api/auth/me';

          const response = await fetch(apiUrl, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
            },
          });

          if (!response.ok) {
            if (response.status === 401) {
              get().clearAuthState();
              return;
            }
            const errorText = await response.text().catch(() => '');
            throw new Error(`Failed to fetch user data (${response.status}): ${errorText}`);
          }

          const userData: AuthUser = await response.json();
          set({ user: userData, isLoggedIn: true });
          get().loadActiveOrganizationFromCookie();
        } catch (error) {
          console.error('[AUTH] Error fetching user data:', error);
          get().clearAuthState();
        } finally {
          if (!silent) set({ authLoading: false });
        }
      },

      patchUser: async (userDataToUpdate: Partial<AuthUser> | Record<string, unknown>, options?: { lang?: string }) => {
        const state = get();
        if (!state.isLoggedIn) {
          return { success: false, message: 'Not authenticated.' };
        }

        set({ authLoading: true });

        const lang = options?.lang && (options.lang === 'en' || options.lang === 'pl') ? options.lang : undefined;
        const patchUrl = lang ? `/api/auth/me?lang=${lang}` : '/api/auth/me';

        try {
          const response = await state.authorizedFetch(patchUrl, {
            method: 'PATCH',
            body: JSON.stringify(userDataToUpdate),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
              success: false,
              message: errorData.detail || 'Failed to update user data.',
            };
          }

          const updatedUserData: AuthUser = await response.json();
          set({ user: updatedUserData });
          return { success: true, message: 'Settings saved successfully.' };
        } catch (error) {
          console.error('Error updating user data:', error);
          return { success: false, message: 'Network error while updating user.' };
        } finally {
          set({ authLoading: false });
        }
      },

      setActiveOrganization: (orgId: string | null) => {
        const current = get().activeOrganizationId;
        if (!orgId) {
          if (current === null) return;
          set({ activeOrganizationId: null });
          Cookies.set('active_org_id', PRIVATE_MODE_COOKIE_VALUE, {
            expires: 30,
          });
          return;
        }

        if (current === orgId) return;

        const membership = get().user?.memberships.find(
          (member) => member.organization.id === orgId,
        );

        if (membership) {
          set({ activeOrganizationId: orgId });
          Cookies.set('active_org_id', orgId, {
            expires: 30,
          });
        } else {
          if (current === null) return;
          set({ activeOrganizationId: null });
          Cookies.set('active_org_id', PRIVATE_MODE_COOKIE_VALUE, {
            expires: 30,
          });
        }
      },

      createOrganization: async (organizationData: {
        name: string;
        slug?: string;
        description?: string;
        vat_number?: string;
        website?: string;
        logo?: File;
        contact_email?: string;
        physical_address?: string;
      }) => {
        const state = get();
        if (!state.isLoggedIn) {
          return { success: false, message: 'Not authenticated.' };
        }

        set({ authLoading: true });

        try {
          // Use FormData to support file upload
          const formData = new FormData();
          formData.append('name', organizationData.name);
          if (organizationData.slug) {
            formData.append('slug', organizationData.slug);
          }
          if (organizationData.description) {
            formData.append('description', organizationData.description);
          }
          if (organizationData.vat_number) {
            formData.append('vat_number', organizationData.vat_number);
          }
          if (organizationData.website) {
            formData.append('website', organizationData.website);
          }
          if (organizationData.contact_email) {
            formData.append('contact_email', organizationData.contact_email);
          }
          if (organizationData.physical_address) {
            formData.append('physical_address', organizationData.physical_address);
          }
          if (organizationData.logo) {
            formData.append('logo', organizationData.logo);
          }

          // Use server action instead of API route
          const { createOrganization: createOrgAction } = await import('@/actions/organizations');
          const result = await createOrgAction(formData);

          if (result.error) {
            return {
              success: false,
              message: result.error,
              fieldErrors: result.fieldErrors,
            };
          }

          const newOrganization = result.data;
          await state.fetchUser();
          return { success: true, data: newOrganization };
        } catch (error) {
          console.error('Error creating organization:', error);
          return { success: false, message: 'Network error while creating organization.' };
        } finally {
          set({ authLoading: false });
        }
      },

      loadActiveOrganizationFromCookie: () => {
        if (typeof window === 'undefined') {
          return;
        }

        const user = get().user;
        if (!user?.memberships || !user.memberships.length) {
          return;
        }

        const current = get().activeOrganizationId;
        const storedId = Cookies.get('active_org_id');

        if (storedId) {
          if (storedId === PRIVATE_MODE_COOKIE_VALUE) {
            if (current !== null) set({ activeOrganizationId: null });
            return;
          }

          const membership = user.memberships.find(
            (member) => member.organization.id === storedId,
          );

          if (membership) {
            if (current !== storedId) set({ activeOrganizationId: storedId });
            return;
          }

          Cookies.set('active_org_id', PRIVATE_MODE_COOKIE_VALUE);
        }

        if (user.memberships && user.memberships.length > 0) {
          const firstOrgId = user.memberships[0]?.organization.id ?? null;
          if (current !== firstOrgId) {
            set({ activeOrganizationId: firstOrgId });
            Cookies.set('active_org_id', firstOrgId ?? PRIVATE_MODE_COOKIE_VALUE);
          }
        } else {
          if (current !== null) set({ activeOrganizationId: null });
        }
      },

      clearAuthState: () => {
        set({
          user: null,
          isLoggedIn: false,
          activeOrganizationId: null,
        });
        
        // Also clear notifications store when auth state is cleared
        if (typeof window !== 'undefined') {
          try {
            // Directly remove LocalStorage items
            localStorage.removeItem('notifications_unread_count');
            localStorage.removeItem('notifications_pending_count');
            localStorage.removeItem('notifications_last_fetched');
            
            // Call store's clearStore function if available
            try {
              useNotificationsStore.getState().clearStore();
            } catch (_e) {
              // Store might not be initialized yet, that's okay
            }
          } catch (error) {
            console.error('[AUTH] Error clearing notifications on auth clear:', error);
          }
        }
      },

      clearAuthCookies: () => {
        // BFF: HttpOnly cookies are managed by backend, we can only clear non-HttpOnly cookies
        Cookies.remove('active_org_id');
        // Note: Session cookies (HttpOnly) are cleared by backend on logout
      },

      logout: async (redirectToLogin = true) => {
        try {
          const { logout } = await import('@/actions/auth');
          await logout();
        } catch (error) {
          console.error('[AUTH] Error calling logout endpoint:', error);
        }
        
        // Clear notifications store on logout - import directly to avoid async issues
        if (typeof window !== 'undefined') {
          try {
            // Directly remove LocalStorage items to be absolutely sure
            localStorage.removeItem('notifications_unread_count');
            localStorage.removeItem('notifications_pending_count');
            localStorage.removeItem('notifications_last_fetched');
            
            // Clear cruises cache
            localStorage.removeItem('local_cruises_cache');
            
            // Clear all cruise draft storage keys
            // Remove cruise_draft_new
            localStorage.removeItem('cruise_draft_new');
            
            // Remove all cruise_draft_edit_* keys
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith('cruise_draft_edit_')) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            // Also call the store's clearStore function
            try {
              useNotificationsStore.getState().clearStore();
            } catch (_e) {
              // Store might not be initialized yet, that's okay
            }
          } catch (error) {
            console.error('[AUTH] Error clearing notifications store:', error);
            // Fallback: manually clear LocalStorage even if store call fails
            if (typeof window !== 'undefined') {
              try {
                localStorage.removeItem('notifications_unread_count');
                localStorage.removeItem('notifications_pending_count');
                localStorage.removeItem('notifications_last_fetched');
                localStorage.removeItem('local_cruises_cache');
                localStorage.removeItem('cruise_draft_new');
                
                // Clear all cruise_draft_edit_* keys in fallback too
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key && key.startsWith('cruise_draft_edit_')) {
                    keysToRemove.push(key);
                  }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
              } catch (e) {
                console.error('[AUTH] Error manually clearing LocalStorage:', e);
              }
            }
          }
        }
        
        get().clearAuthState();
        get().clearAuthCookies();

        if (redirectToLogin && typeof window !== 'undefined') {
          const path = window.location.pathname + window.location.search || '/';
          const returnUrl = path === '/login' ? '' : encodeURIComponent(path);
          window.location.href = returnUrl ? `/login?returnUrl=${returnUrl}` : '/login';
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // BFF: Only persist user data and login state, not tokens
        // Session is managed by backend via HttpOnly cookies
        user: state.user,
        isLoggedIn: state.isLoggedIn,
        activeOrganizationId: state.activeOrganizationId,
      }),
    }
  )
);
